import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { accountService } from '@exchange/libs/account/service/src';
import { partnersService } from '@exchange/libs/partners/service/src';

export interface NavigationLink {
  classModifier?: string;
  external?: boolean;
  icon?: string;
  iconWithWarning?: boolean;
  iconWithInfo?: boolean;
  id: string;
  route?: { name: string; params?: Dictionary<unknown> };
  title: string;
  pageTitle?: string;
  url?: string;
}

const useNavigationLinks = () => {
  const { t } = useI18n({ useScope: 'global' });

  const { showPartnersDot } = partnersService;
  const { showSecurityWarning } = accountService;

  const getTitle = (name: string) => t(`modules.userMenu.${name}`);

  const navLinks = computed(() => {
    const settings: NavigationLink = {
      icon: 'setting',
      iconWithWarning: showSecurityWarning.value,
      classModifier: 'cog',
      title: getTitle('settings'),
      pageTitle: t('modules.account.settings.title'),
      external: false,
      route: { name: 'settings.general' },
      id: 'settings',
    };

    const levels: NavigationLink = {
      icon: '3square',
      classModifier: 'levels',
      title: getTitle('feeTiers'),
      pageTitle: t('modules.tiers.pageHeader'),
      route: { name: 'fee-levels' },
      external: false,
      id: 'fee-levels',
    };

    const partners: NavigationLink = {
      icon: 'medal-star',
      classModifier: showPartnersDot.value ? 'has-green-dot' : 'partners',
      iconWithInfo: showPartnersDot.value,
      title: getTitle('promotions'),
      pageTitle: t('modules.account.partners.title'),
      external: false,
      route: { name: 'promotions.list' },
      id: 'partners',
    };

    const tradingAccounts: NavigationLink = {
      icon: 'row-vertical',
      classModifier: 'trading-accounts',
      title: getTitle('tradingAccounts'),
      external: false,
      route: { name: 'trading-accounts.balances' },
      id: 'trading-accounts',
    };
    const exports: NavigationLink = {
      icon: 'received',
      classModifier: 'push-to-right',
      title: getTitle('exports'),
      pageTitle: t('modules.exports.page.title'),
      external: false,
      route: { name: 'exports' },
      id: 'exports',
    };

    const help: NavigationLink = {
      icon: 'book',
      title: getTitle('support'),
      url: getTitle('helpUrl'),
      external: true,
      id: 'help',
    };
    const blog: NavigationLink = {
      icon: 'edit',
      title: getTitle('blog'),
      url: getTitle('blogUrl'),
      external: true,
      id: 'blog',
    };
    const legal: NavigationLink = {
      icon: 'note-text',
      title: getTitle('legalNotice'),
      url: getTitle('legalNoticeUrl'),
      external: true,
      id: 'legal-notice',
    };
    const keyInformationDocuments: NavigationLink = {
      icon: 'file-text',
      title: getTitle('keyInformationDocuments'),
      url: getTitle('keyInformationDocumentsUrl'),
      external: true,
      id: 'key-information-documents',
    };

    return {
      blog,
      exports,
      help,
      legal,
      levels,
      partners,
      settings,
      tradingAccounts,
      keyInformationDocuments,
    };
  });

  return {
    navLinks,
  };
};

export default useNavigationLinks;
